/**
 * Leave unit helpers for the dashboard.
 *
 * These mirror api/utils/leaveBalance.js so a cost the form PREVIEWS and a cost
 * the API CHARGES are the same number. If the two ever disagree, somebody is
 * told they are asking for 3 days and then billed 5, and they will be right to
 * not believe the screen again.
 *
 * The rules being mirrored, and why each matters:
 *
 *   • A day is that person's OWN shift length, not 8 hours. 8 hours off a
 *     4-hour shift is two working days.
 *   • A date range costs only the person's WORKING days. A week off a
 *     three-day-a-week carer is 3 days, not 7.
 *   • Both facts arrive on the balance payload (`day_hours`, `working_days`) —
 *     they are never assumed here. An empty `working_days` means no pattern is
 *     on file and every calendar day counts, which is the same reading the
 *     server's countWorkingDays applies.
 *
 * Anything more than presentation belongs on the server. These exist so the
 * form can say something truthful before it posts, not so the client can decide
 * what leave costs.
 */

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const round2 = (n) => Number(Number(n || 0).toFixed(2));

/** Trim a trailing .00 / .50 → "3", "3.5". Balances read as figures, not decimals. */
const tidy = (n) => String(round2(n)).replace(/\.0+$/, '').replace(/(\.\d)0$/, '$1');

/**
 * A quantity in the organisation's unit, e.g. "2.5 days" or "10 hours".
 *
 * Always takes DAYS and converts, because days are what the allowance is kept
 * in — see api/utils/leaveBalance.js. Passing hours in here and expecting hours
 * out is the mistake this signature is shaped to prevent.
 */
export const quantityLabel = (days, dayHours, unit) => {
  const perDay = dayHours || 8;
  const value = unit === 'hours' ? round2(Number(days) * perDay) : round2(days);
  const noun = unit === 'hours' ? 'hour' : 'day';
  return `${tidy(value)} ${value === 1 ? noun : `${noun}s`}`;
};

/** Weekday name for a YYYY-MM-DD string, read in UTC so it never drifts a day. */
const weekdayOf = (dateStr) => {
  const ts = Date.parse(`${String(dateStr).substring(0, 10)}T00:00:00Z`);
  if (Number.isNaN(ts)) return null;
  return DAY_NAMES[new Date(ts).getUTCDay()];
};

/**
 * What a block of hours costs: `{ days, hours, note }`.
 *
 * `note` is the arithmetic spelled out, and only when it is surprising — 8 hours
 * against a 4-hour shift needs explaining, 4 against 4 does not. Suppressing it
 * in the obvious case is what keeps it worth reading in the odd one.
 */
export const costOfHours = (hours, dayHours) => {
  const perDay = dayHours || 8;
  const days = round2(Number(hours) / perDay);
  return {
    days,
    hours: round2(hours),
    note: days > 1
      ? `${tidy(hours)} hours against your ${tidy(perDay)}-hour shift is ${tidy(days)} working days`
      : null,
  };
};

/**
 * What an inclusive date range costs: `{ days, hours, note }`.
 *
 * Counts only the person's working days. `note` calls out the difference when
 * some days in the range aren't theirs to take off — that gap is the number
 * people query, so it is better stated than discovered.
 *
 * The loop is capped for the same reason the server's is: a nonsense range must
 * not hang the form.
 */
export const costOfRange = (startDate, endDate, dayHours, workingDays = []) => {
  const perDay = dayHours || 8;
  const pattern = (workingDays || []).map((d) => String(d).toLowerCase());

  let working = 0;
  let calendar = 0;
  let cursor = String(startDate).substring(0, 10);
  const end = String(endDate).substring(0, 10);

  for (let i = 0; i < 800 && cursor <= end; i += 1) {
    calendar += 1;
    // No pattern on file: every day counts, matching the server's fallback.
    if (!pattern.length || pattern.includes(weekdayOf(cursor))) working += 1;
    cursor = new Date(Date.parse(`${cursor}T00:00:00Z`) + 86400000)
      .toISOString().substring(0, 10);
  }

  return {
    days: round2(working),
    hours: round2(working * perDay),
    note: pattern.length && working !== calendar
      ? `${calendar} days selected, ${tidy(working)} of them yours to work`
      : null,
  };
};
